import { AxiosResponse } from 'axios';
import { apiClient, uploadClient } from '../config/httpClient';
import { InformePericial, InformeFormData, Cita, CitaFormData, Videoperitacion } from '../../types';
import { UploadResponse, TranscribeResponse, AnalysisResponse } from '../types';

export class InformeService {
    listarInformes(): Promise<AxiosResponse<InformePericial[]>> {
        return apiClient.get<InformePericial[]>('/informes');
    }

    obtenerInforme(id: number): Promise<AxiosResponse<InformePericial>> {
        return apiClient.get<InformePericial>(`/informes/${id}`);
    }

    crearInforme(data: InformeFormData): Promise<AxiosResponse<InformePericial>> {
        return apiClient.post<InformePericial>('/informes', data);
    }

    actualizarInforme(id: number, data: Partial<InformeFormData>): Promise<AxiosResponse<InformePericial>> {
        return apiClient.put<InformePericial>(`/informes/${id}`, data);
    }

    eliminarInforme(id: number): Promise<AxiosResponse<{ message: string }>> {
        return apiClient.delete(`/informes/${id}`);
    }

    filtrarInformes(numero_siniestro?: string, numero_poliza?: string): Promise<AxiosResponse<InformePericial[]>> {
        return apiClient.get<InformePericial[]>('/informes/filtrar', {
            params: { numero_siniestro, numero_poliza }
        });
    }

    uploadGrabacion(formData: FormData): Promise<AxiosResponse<UploadResponse>> {
        return uploadClient.post<UploadResponse>('/uploads', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    generarPDF(id: number): Promise<AxiosResponse<Blob>> {
        return apiClient.post(`/informes/${id}/pdf`, {}, {
            responseType: 'blob'
        });
    }

    transcribirAudio(data: { audioData: string }): Promise<AxiosResponse<TranscribeResponse>> {
        return apiClient.post<TranscribeResponse>('/informes/transcribe', data);
    }

    analizarCobertura(data: { descripcion: string; transcripcion?: string }): Promise<AxiosResponse<AnalysisResponse>> {
        return apiClient.post<AnalysisResponse>('/informes/analyze', data);
    }

    crearCita(id: number, data: CitaFormData): Promise<AxiosResponse<Cita>> {
        return apiClient.post<Cita>(`/informes/${id}/cita`, data);
    }

    obtenerCita(id: number): Promise<AxiosResponse<Cita>> {
        return apiClient.get<Cita>(`/informes/${id}/cita`);
    }

    actualizarCita(id: number, data: CitaFormData): Promise<AxiosResponse<Cita>> {
        return apiClient.put<Cita>(`/informes/${id}/cita`, data);
    }

    subirVideoperitacion(id: number, files: File[]): Promise<AxiosResponse<Videoperitacion>> {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('pdf', file);
        });
        return apiClient.post<Videoperitacion>(`/informes/${id}/videoperitacion`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    obtenerVideoperitacion(id: number): Promise<AxiosResponse<Videoperitacion>> {
        return apiClient.get<Videoperitacion>(`/informes/${id}/videoperitacion`);
    }

    eliminarArchivoVideoperitacion(id: number, url: string): Promise<AxiosResponse<Videoperitacion>> {
        return apiClient.delete<Videoperitacion>(`/informes/${id}/videoperitacion/archivo`, {
            params: { url }
        });
    }

    generarInformeIA(id: number): Promise<AxiosResponse<{ success: boolean; status?: string; informe?: InformePericial }>> {
        return apiClient.post(`/informes/${id}/generar`);
    }

    generarCapituloIA(id: number, capituloKey: string): Promise<AxiosResponse<InformePericial>> {
        return apiClient.post<InformePericial>(`/informes/${id}/generar-capitulo/${capituloKey}`);
    }

    getEstadoGeneracionIA(id: number): Promise<AxiosResponse<{ estado_ia: string; capitulo_datos_cliente?: string; capitulo_descripcion_siniestro?: string; capitulo_riesgo_asegurado?: string; capitulo_calculo_capitales?: string; informe_markdown: string | null }>> {
        return apiClient.get(`/informes/${id}/estado-ia`);
    }

    actualizarInformeMarkdown(id: number, markdown?: string, tabla_valoracion?: import('../../types').PartidaValoracion[], capitulos?: { capitulo_datos_cliente?: string; capitulo_descripcion_siniestro?: string; capitulo_riesgo_asegurado?: string; capitulo_calculo_capitales?: string }): Promise<AxiosResponse<InformePericial>> {
        return apiClient.put<InformePericial>(`/informes/${id}/informe-markdown`, { markdown, tabla_valoracion, ...capitulos });
    }

    enviarInformeEmail(id: number, email: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
        return apiClient.post(`/informes/${id}/enviar`, { email });
    }

    descargarDocx(id: number, templateId?: number): Promise<AxiosResponse<Blob>> {
        return apiClient.get(`/informes/${id}/descargar-docx`, {
            responseType: 'blob',
            params: templateId ? { template_id: templateId } : {}
        });
    }

    sincronizarGmail(): Promise<AxiosResponse<{ success: boolean; message: string; count: number }>> {
        return apiClient.post(`/gmail/sync`);
    }

    subirGrabacionPerito(id: number, file: File): Promise<AxiosResponse<Videoperitacion>> {
        const formData = new FormData();
        formData.append('audio', file);
        return apiClient.post<Videoperitacion>(`/informes/${id}/grabacion-perito`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    obtenerGrabacionPerito(id: number): Promise<AxiosResponse<{ grabado: boolean; transcripcion: string | null; audio_url: string | null }>> {
        return apiClient.get(`/informes/${id}/grabacion-perito`);
    }

    cambiarFase(id: number, fase: string): Promise<AxiosResponse<InformePericial>> {
        return apiClient.put<InformePericial>(`/informes/${id}/fase`, { fase });
    }

    actualizarGrabacionTranscripcion(id: number, transcripcion: string): Promise<AxiosResponse<{ success: boolean; transcripcion: string }>> {
        return apiClient.put(`/informes/${id}/grabacion-perito/transcripcion`, { transcripcion });
    }

    eliminarGrabacionPerito(id: number): Promise<AxiosResponse<{ success: boolean }>> {
        return apiClient.delete(`/informes/${id}/grabacion-perito`);
    }

    buscarFechaLibre(id: number): Promise<AxiosResponse<{ fecha: string; hora: string }>> {
        return apiClient.get<{ fecha: string; hora: string }>(`/informes/${id}/cita/buscar-fecha`);
    }

    iniciarCitacion(id: number): Promise<AxiosResponse<Cita>> {
        return apiClient.post<Cita>(`/informes/${id}/cita/iniciar-citacion`);
    }

    // --- Plazos de Entrega por Compañía ---
    obtenerPlazosCompania(): Promise<AxiosResponse<{ id: number; compania: string; dias_limite_entrega: number }[]>> {
        return apiClient.get('/configuracion/plazos-compania');
    }

    guardarPlazoCompania(data: { compania: string; dias_limite_entrega: number }): Promise<AxiosResponse<{ id: number; compania: string; dias_limite_entrega: number }>> {
        return apiClient.post('/configuracion/plazos-compania', data);
    }

    eliminarPlazoCompania(id: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
        return apiClient.delete(`/configuracion/plazos-compania/${id}`);
    }

    // --- Tabla de Valoración del Informe ---
    obtenerTablaValoracion(id: number): Promise<AxiosResponse<{ items: any[] }>> {
        return apiClient.get<{ items: any[] }>(`/informes/${id}/valoracion`);
    }

    guardarTablaValoracion(id: number, items: any[]): Promise<AxiosResponse<{ items: any[] }>> {
        return apiClient.put<{ items: any[] }>(`/informes/${id}/valoracion`, { items });
    }

    exportarValoracionExcel(id: number): Promise<AxiosResponse<{ mensaje: string; archivo: string; descarga_url: string }>> {
        return apiClient.post(`/informes/${id}/valoracion/exportar-excel`);
    }

    descargarValoracionExcel(id: number): Promise<AxiosResponse<Blob>> {
        return apiClient.get(`/informes/${id}/valoracion/descargar-excel`, {
            responseType: 'blob'
        });
    }

    importarValoracionExcel(id: number, file: File): Promise<AxiosResponse<{ mensaje: string; items: any[] }>> {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post<{ mensaje: string; items: any[] }>(`/informes/${id}/valoracion/importar-excel`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    // --- Gestión de Plantillas ---
    obtenerTemplates(): Promise<AxiosResponse<any[]>> {
        return apiClient.get<any[]>('/configuracion/templates');
    }

    subirTemplate(formData: FormData): Promise<AxiosResponse<any>> {
        return apiClient.post<any>('/configuracion/templates', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    actualizarTemplate(id: number, data: { nombre: string }): Promise<AxiosResponse<any>> {
        return apiClient.put<any>(`/configuracion/templates/${id}`, data);
    }

    eliminarTemplate(id: number): Promise<AxiosResponse<any>> {
        return apiClient.delete<any>(`/configuracion/templates/${id}`);
    }

    sincronizarGmailProgress(onProgress: (data: { current: number; total: number; message: string; completed?: boolean; count?: number }) => void): Promise<void> {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        const token = localStorage.getItem('token');
        return new Promise((resolve, reject) => {
            fetch(`${baseURL}/gmail/sync-stream`, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }).then(async (response) => {
                if (!response.ok) {
                    const errorText = await response.text();
                    let errMsg = 'Error al conectar con la sincronización de Gmail';
                    try {
                        const parsedErr = JSON.parse(errorText);
                        errMsg = parsedErr.detail || parsedErr.error || errMsg;
                    } catch (e) {}
                    throw new Error(errMsg);
                }
                const reader = response.body?.getReader();
                if (!reader) throw new Error('No se pudo leer la respuesta del servidor');

                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const parsed = JSON.parse(line.trim());
                                onProgress(parsed);
                            } catch (e) {
                                console.error('Error parseando JSON de progreso Gmail:', e);
                            }
                        }
                    }
                }
                resolve();
            }).catch(reject);
        });
    }
}

export const informeService = new InformeService();
